import {
  createSlice,
  type PayloadAction,
  type Reducer,
} from '@reduxjs/toolkit';
import type { RootState } from '@root/store/store';
import type { RunFundingStatus, RunFundingStatusDTO } from '@root/store/types';

export const FUNDING_SLICE_KEY = 'funding';

export interface FundingSliceState {
  status: RunFundingStatus | null;
  journeyId: string | null;
  // Absolute ISO time the run auto-abandons. Derive the countdown, never store it.
  resumeDeadline: string | null;
  shortfallAmount: number | null;
  currencyCode: string | null;
  // A resume has been sent and we're waiting for the server to confirm.
  resuming: boolean;
  // Holds the roll until the pause notice arrives on the other channel.
  shortBalance: boolean;
  // The player closed the dialog. The round stays paused behind it.
  dialogDismissed: boolean;
}

export const initialState: FundingSliceState = {
  status: null,
  journeyId: null,
  resumeDeadline: null,
  shortfallAmount: null,
  currencyCode: null,
  resuming: false,
  shortBalance: false,
  dialogDismissed: false,
};

const fundingSlice = createSlice({
  name: FUNDING_SLICE_KEY,
  initialState,
  reducers: {
    setFundingStatus: (state, action: PayloadAction<RunFundingStatusDTO>) => {
      const dto = action.payload;
      state.status = dto.status;
      state.journeyId = dto.journeyId;
      state.resumeDeadline = dto.resumeDeadline ?? null;
      state.shortfallAmount = dto.shortfallAmount ?? null;
      state.currencyCode = dto.currencyCode ?? null;
      // Any status at all answers a pending resume.
      state.resuming = false;
      state.shortBalance = dto.status === 'NEEDS_FUNDING';
      // A pause they already dismissed isn't new.
      if (dto.status !== 'NEEDS_FUNDING') state.dialogDismissed = false;
    },
    // Don't wait for the pause notice to stop the turn.
    setShortBalance: (state, action: PayloadAction<boolean>) => {
      state.shortBalance = action.payload;
      if (action.payload) state.dialogDismissed = false;
    },
    dismissFundingDialog: (state) => {
      state.dialogDismissed = true;
    },
    showFundingDialog: (state) => {
      state.dialogDismissed = false;
    },
    setResuming: (state, action: PayloadAction<boolean>) => {
      state.resuming = action.payload;
    },
    clearFunding: () => initialState,
  },
});

export const fundingSelector = (state: RootState): FundingSliceState =>
  state[FUNDING_SLICE_KEY];

// Paused and still in the window. Either channel can tell us first.
export const isRunPaused = (state: RootState): boolean => {
  const funding = state[FUNDING_SLICE_KEY];
  return funding.shortBalance || funding.status === 'NEEDS_FUNDING';
};

// The run is over by funding, not by play.
export const isRunClosedByFunding = (state: RootState): boolean => {
  const status = state[FUNDING_SLICE_KEY].status;
  return status === 'ABANDONED' || status === 'EXPIRED';
};

export const fundingActions = fundingSlice.actions;
export default fundingSlice.reducer as Reducer<FundingSliceState>;
